// src/api/inventory/salesOrderApi.ts
import { ENV } from '../../config/runtimeEnv';
import type { PublicSalesOrder, SalesOrder, SalesOrderFilters } from '../../types/inventory/OrderTypes';
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_COMMERCE_SERVER;

/**
 * Emitted by the backend ONLY when a search returned zero orders and the term
 * matches at least one stock label. The key is absent — never null — otherwise,
 * so the UI keys off presence.
 */
export interface SalesOrderLabelLookup {
	/** The trimmed term as actually searched. */
	term: string;
	/** How many stock labels match it. Always > 0 when present. */
	matches: number;
}

export const fetchSalesOrders = async (params?: SalesOrderFilters): Promise<{ data: SalesOrder[]; total: number; labelLookup?: SalesOrderLabelLookup }> => {
	const response = await http.get(`${API_BASE_URL}/sales-orders`, {
		params,
	});
	return { data: response.data.data, total: response.data.pagination?.total_items || 0, labelLookup: response.data.label_lookup };
};

export const fetchSalesOrder = async (id: number): Promise<SalesOrder> => {
	const response = await http.get(`${API_BASE_URL}/sales-orders/${id}`);
	return response.data;
};

// The kit-assignment capability: a header, so the token stays out of access logs (commerce also accepts `?t=`).
const assignmentTokenHeaders = (token?: string) => (token ? { "X-Assignment-Token": token } : undefined);

export const fetchSalesOrderPublic = async (id: number, token?: string): Promise<PublicSalesOrder> => {
	const response = await http.get(`${API_BASE_URL}/public/sales-order/${id}`, {
		headers: assignmentTokenHeaders(token),
	});
	return response.data;
};

export interface AssignmentLink {
	sales_order_id: number;
	/** Same-origin path carrying the new token; every mint replaces the order's earlier link. */
	path: string;
}

const ASSIGNMENT_PATH_PREFIX = "/patient/test-order-assignments/";

export const mintAssignmentLink = async (salesOrderId: number): Promise<AssignmentLink> => {
	const response = await http.post(`${API_BASE_URL}/sales-orders/${salesOrderId}/assignment-link`);
	const body: unknown = response.data;
	const { sales_order_id, path } = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
	if (sales_order_id !== salesOrderId || typeof path !== "string" || !path.startsWith(ASSIGNMENT_PATH_PREFIX)) {
		throw new Error("Unexpected assignment-link response");
	}
	return { sales_order_id: salesOrderId, path };
};

export const createSalesOrder = async (orderData: SalesOrder): Promise<SalesOrder> => {
	const response = await http.post(`${API_BASE_URL}/sales-orders`, orderData);
	return response.data;
};

export const updateSalesOrder = async (id: number, orderData: Partial<SalesOrder>): Promise<SalesOrder> => {
	const response = await http.put(`${API_BASE_URL}/sales-orders/${id}`, orderData);
	return response.data;
};

export const deleteSalesOrder = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/sales-orders/${id}`);
};